import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Network, Mail, Lock, Eye, EyeOff, Building2 } from 'lucide-react';
interface LoginPageProps {
  onLogin: (email: string) => void;
}
export function LoginPage({
  onLogin
}: LoginPageProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyCode, setCompanyCode] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onLogin(email);
  };
  return <div className="min-h-screen flex flex-col lg:flex-row bg-slate-50" data-id="element-720">
      {/* Left Panel - Branding & Context */}
      <div className="lg:w-5/12 xl:w-1/2 bg-brand-navy text-white flex flex-col justify-between p-4 xs:p-6 sm:p-8 lg:p-12 shrink-0 relative overflow-hidden" data-id="element-721">
        {/* Subtle background pattern/glow for enterprise feel */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-722">
          <div className="absolute -top-[20%] -left-[10%] w-[70%] h-[70%] rounded-full bg-brand-teal/5 blur-[120px]" data-id="element-723" />
          <div className="absolute bottom-[10%] -right-[20%] w-[60%] h-[60%] rounded-full bg-blue-500/5 blur-[100px]" data-id="element-724" />
        </div>

        {/* Header / Logo */}
        <div className="flex items-center gap-2 sm:gap-3 relative z-10" data-id="element-725">
          <div className="bg-brand-teal/10 p-2 rounded-xl border border-brand-teal/20" data-id="element-726">
            <Network className="w-6 h-6 xs:w-7 xs:h-7 sm:w-8 sm:h-8 text-brand-teal" data-id="element-727" />
          </div>
          <span className="text-xl xs:text-2xl sm:text-3xl font-bold tracking-tight" data-id="element-728">
            ERPLogiPark
          </span>
        </div>

        {/* Desktop Tagline & Illustration */}
        <div className="hidden lg:block mt-auto mb-auto pr-8 relative z-10" data-id="element-729">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-730">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-navyLight border border-slate-700/50 text-brand-teal text-sm font-medium mb-6" data-id="element-731">
              <span className="relative flex h-2 w-2" data-id="element-732">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-teal opacity-75" data-id="element-733"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-teal" data-id="element-734"></span>
              </span>
              System Operational
            </div>
            <h1 className="text-4xl xl:text-5xl font-bold leading-[1.15] mb-6 text-white" data-id="element-735">
              Terminal Management & <br className="hidden xl:block" data-id="element-736" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-teal to-emerald-300" data-id="element-737">
                Intelligent Gate Automation
              </span>
            </h1>
            <p className="text-slate-400 text-lg xl:text-xl leading-relaxed max-w-lg" data-id="element-738">
              Automate gate operations, manage terminal workflows, and optimize
              vehicle flow from a single, unified enterprise platform.
            </p>
          </motion.div>
        </div>

        {/* Desktop Footer */}
        <div className="hidden lg:block text-slate-500 text-sm relative z-10" data-id="element-739">
          © 2026 ERPLogiPark. All rights reserved.
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-3 xs:p-4 sm:p-8 lg:p-12 bg-slate-50 relative z-10" data-id="element-740">
        <div className="w-full max-w-md" data-id="element-741">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 p-5 xs:p-6 sm:p-8" data-id="element-742">
            <div className="mb-6 sm:mb-8" data-id="element-743">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2 tracking-tight" data-id="element-744">
                Welcome back
              </h2>
              <p className="text-sm sm:text-base text-slate-500" data-id="element-745">
                Enter your credentials to access your dashboard.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5" data-id="element-746">
              {/* Email / Username Field */}
              <div data-id="element-747">
                <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-748">
                  Email or Username
                </label>
                <div className="relative" data-id="element-749">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none" data-id="element-750">
                    <Mail className="h-5 w-5 text-slate-400" data-id="element-751" />
                  </div>
                  <input type="text" value={email} onChange={e => setEmail(e.target.value)} className="block w-full pl-11 pr-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-brand-teal/20 focus:border-brand-teal text-slate-900 placeholder-slate-400 transition-all min-h-[48px] text-base sm:text-sm" placeholder="admin@erplogipark.com" required data-id="element-752" />
                </div>
              </div>

              {/* Password Field */}
              <div data-id="element-753">
                <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-754">
                  Password
                </label>
                <div className="relative" data-id="element-755">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none" data-id="element-756">
                    <Lock className="h-5 w-5 text-slate-400" data-id="element-757" />
                  </div>
                  <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} className="block w-full pl-11 pr-12 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-brand-teal/20 focus:border-brand-teal text-slate-900 placeholder-slate-400 transition-all min-h-[48px] text-base sm:text-sm" placeholder="••••••••" required data-id="element-758" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 w-12 flex items-center justify-center text-slate-400 hover:text-slate-600 focus:outline-none focus:text-brand-teal transition-colors rounded-r-xl" aria-label={showPassword ? 'Hide password' : 'Show password'} data-id="element-759">
                    {showPassword ? <EyeOff className="h-5 w-5" data-id="element-760" /> : <Eye className="h-5 w-5" data-id="element-761" />}
                  </button>
                </div>
              </div>

              {/* Company Code Field */}
              <div data-id="element-762">
                <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-763">
                  Company Code
                </label>
                <div className="relative" data-id="element-764">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none" data-id="element-765">
                    <Building2 className="h-5 w-5 text-slate-400" data-id="element-766" />
                  </div>
                  <input type="text" value={companyCode} onChange={e => setCompanyCode(e.target.value)} className="block w-full pl-11 pr-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-brand-teal/20 focus:border-brand-teal text-slate-900 placeholder-slate-400 transition-all min-h-[48px] text-base sm:text-sm" placeholder="e.g. ELP-001" required data-id="element-767" />
                </div>
              </div>

              {/* Options Row */}
              <div className="flex items-center justify-between pt-1" data-id="element-768">
                <div className="flex items-center min-h-[48px]" data-id="element-769">
                  <input id="remember-me" type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} className="h-5 w-5 sm:h-4 sm:w-4 text-brand-teal focus:ring-brand-teal border-slate-300 rounded cursor-pointer transition-colors" data-id="element-770" />
                  <label htmlFor="remember-me" className="ml-2.5 block text-sm text-slate-700 cursor-pointer select-none" data-id="element-771">
                    Remember me
                  </label>
                </div>
                <a href="#" className="text-sm font-medium text-brand-teal hover:text-brand-tealHover transition-colors min-h-[48px] flex items-center px-2 -mr-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal/20" data-id="element-772">
                  Forgot password?
                </a>
              </div>

              {/* Submit Button */}
              <button type="submit" className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-base font-semibold text-white bg-brand-teal hover:bg-brand-tealHover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-teal transition-all min-h-[48px] mt-2 active:scale-[0.98]" data-id="element-773">
                Sign in to ERPLogiPark
              </button>
            </form>
          </motion.div>

          {/* Mobile Footer */}
          <div className="mt-8 text-center lg:hidden text-slate-500 text-xs sm:text-sm px-4" data-id="element-774">
            © 2026 ERPLogiPark. All rights reserved.
          </div>
        </div>
      </div>
    </div>;
}